import { useMemo, type ReactNode } from 'react';

import { env } from '@/env';
import { getLogtoRolesFromAccessToken } from '@/lib/logto';
import { useAppSelector } from '@/store/hooks';
import { AccessDenied } from './AccessDenied';

interface AdminGateProps {
  children: ReactNode;
}

/**
 * Roles on Logto's PROD tenant that grant access to a gated build. Mirrors
 * what's set up in `admin-account.qlash.gg → Ruoli`: `administrator` is the
 * full-admin grant, `beta` lets beta testers into the DEV/staging build.
 */
const PRIVILEGED_LOGTO_ROLES = ['administrator', 'beta'] as const;

/**
 * Build-level access gate. When `env.adminGateEnabled === true` (DEV build,
 * `@QLASH_DEV_bot` at `dev-tma.qlash.gg`), only users carrying at least one
 * of the privileged Logto roles proceed; everyone else sees <AccessDenied />.
 *
 * Roles are read from the Logto access token's `roles` claim (injected by
 * the PROD tenant's Custom JWT script — see
 * `getLogtoRolesFromAccessToken` for the why). We do NOT read `is_admin` /
 * `beta_tester` from Laravel `/users/me` because Laravel doesn't expose
 * those fields to mini-app callers.
 *
 * The PROD build (`tma.qlash.gg`) leaves `adminGateEnabled === false`, so
 * this component renders its children unconditionally for all users.
 *
 * Mirrors the `?dev=1` bypass from <AppShell /> so the dev experience works
 * locally even without privileged Logto roles.
 *
 * Placement: this gate sits AFTER the auth gate in <AppShell /> — when
 * `bridgeAuthenticated === false`, the user is shown <Login /> before this
 * component is ever evaluated.
 */
export function AdminGate({ children }: AdminGateProps) {
  // bridgeAuthenticated flips when the access token is stored/cleared, so
  // re-evaluating roles on its change is enough; we don't need to subscribe
  // to localStorage directly.
  const authed = useAppSelector((s) => s.auth.bridgeAuthenticated);

  const isPrivileged = useMemo(() => {
    if (!authed) return false;
    const roles = getLogtoRolesFromAccessToken();
    return PRIVILEGED_LOGTO_ROLES.some((r) => roles.includes(r));
  }, [authed]);

  const devBypass =
    import.meta.env.DEV && new URLSearchParams(window.location.search).has('dev');

  if (!env.adminGateEnabled || devBypass) {
    return <>{children}</>;
  }

  if (!isPrivileged) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}
